import { DropdownOption, mapToDropdownOptionArray } from 'components/final-form/dropdownUtils';
import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Divider, PaginationProps } from 'semantic-ui-react';
import { withRouterWorkaround } from 'utils/workaroundUtils';
import styled from 'styled-components';
import { RouterProps } from 'react-router';
import { DayStatisticRequest, DayStatisticResponse, TariffDto } from 'ts-types/api.types';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import { getTariffs } from 'services/tariffServices';
import { noop } from 'utils/functionUtils';
import { CancelTokenSource } from 'axios';
import axios from 'services/http';
import DayStatisticSearchView from 'components/statistic/day-statistic/DayStatisticSearchView';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import { getAllTreaters } from 'services/treaterServices';
import DayStatisticTableSegment from 'components/statistic/day-statistic/DayStatisticTableSegment';
import { DayStatisticsOptions, ProceduresProtocolSortBy } from 'ts-types/api.enums';
import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';

const StyledContainer = styled.div`
  flex: 1 1 auto;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

export interface ExtendedDayStatisticRequest extends DayStatisticRequest {
  inactiveTariffs: boolean,
  treaterSelection: boolean
}

interface Props extends MessageSourceProps, AuthConsumerRenderProps, RouterProps {
}

interface State {
  statisticsLoading: boolean,
  statisticsData: DayStatisticResponse,
  searchData: Partial<ExtendedDayStatisticRequest>
  tariffs: Array<TariffDto>,
  treaters: Array<DropdownOption>,
  activePage?: number | string,
  errorMessage?: string,
  cancelTokenSource: CancelTokenSource
}

class DayStatisticView extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();
    const {getMessage} = this.props;

    this.state = {
      statisticsData: this.initialDayStatisticDataValues(),
      statisticsLoading: false,
      searchData: this.initialSearchValues(),
      tariffs: [],
      treaters: [],
      cancelTokenSource: cancelTokenSource
    }

    Promise.all([getTariffs(cancelTokenSource), getAllTreaters(cancelTokenSource)])
        .then(responses => {

          this.setState({
            tariffs: responses[0],
            treaters: mapToDropdownOptionArray(responses[1], "username", "id")
          })
        })
        .catch(() => {
          this.setErrorMessage(getMessage("error.general"));
        })
        .finally(noop);
  }

  initialSearchValues = (): Partial<ExtendedDayStatisticRequest> => {

    const {currentUser, options: {workDate}} = this.props;

    return {
      mandateId: currentUser ? currentUser.userConfig.mandateId : undefined,
      fromDate: workDate,
      toDate: workDate,
      tariffIds: [],
      billStatus: 1,
      option: DayStatisticsOptions.TURN_OVER,
      sortBy: ProceduresProtocolSortBy.TIME,
      inactiveTariffs: false,
      treaterSelection: false,
      treaterId: undefined
    }
  };

  initialDayStatisticDataValues = (): DayStatisticResponse => {
    return {
      tariffStatistics: [],
      total: 0
    }
  };

  onFilterStatistics = (statisticsData: DayStatisticResponse) => {
    if (statisticsData.tariffStatistics.length > 0) {
      this.setState({
        activePage: 1
      })
    }
    this.setState({
      statisticsData: statisticsData
    })
  };

  onStatisticsLoadingChange = (statisticsLoading: boolean) => {
    this.setState({
      statisticsLoading: statisticsLoading
    })
  };

  setSearchData = (searchData: Partial<ExtendedDayStatisticRequest>) => {
    this.setState({
      searchData: searchData
    })
  };

  handlePaginationChange = (e: any, {activePage}: PaginationProps) => this.setState({activePage: activePage});

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    });
  };

  render(): React.ReactNode {

    const {statisticsData, statisticsLoading, treaters, tariffs, activePage, searchData, errorMessage} = this.state;

    return (
        <PraxStyledSegment>
          <StyledContainer>
            {errorMessage
            && (
                <StyledErrorMessage onDismiss={() => this.setErrorMessage()} style={{margin: 0}}>
                  {errorMessage}
                </StyledErrorMessage>
            )
            }
            <DayStatisticSearchView
                onFilterStatistics={this.onFilterStatistics}
                onStatisticsLoadingChange={this.onStatisticsLoadingChange}
                searchData={searchData}
                setSearchData={this.setSearchData}
                tariffs={tariffs}
                treaters={treaters}
                setErrorMessage={this.setErrorMessage}
            />
            <Divider/>
            <DayStatisticTableSegment
                statisticsData={statisticsData}
                statisticsLoading={statisticsLoading}
                tariffs={tariffs}
                activePage={activePage}
                handlePaginationChange={this.handlePaginationChange}
                setErrorMessage={this.setErrorMessage}
            />
          </StyledContainer>
        </PraxStyledSegment>
    );
  }
}

export default withRouterWorkaround(withAuthContext(withMessages(DayStatisticView)));
